import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { RuleScope } from '@app/shared/components/rule-scope/rule-scope.component';
import { NotificationType } from '@app/shared/interfaces';
import { HostsService } from '@app/shared/services/hosts/hosts.service';
import { PlatformsService } from '@app/shared/services/platforms/platforms.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { InboxAIAutoreplyMode, InboxAISettings, InboxAISettingsGroup, InboxAIThreadStage, SettingsService } from '@app/shared/services/settings/settings.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'sbnb-gx-inbox-ai-settings',
  templateUrl: './inbox-ai-settings.component.html',
  styleUrls: ['./inbox-ai-settings.component.scss'],
})
export class InboxAiSettingsComponent implements OnInit, OnDestroy {
  readonly allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  public readonly InboxAIAutoreplyMode = InboxAIAutoreplyMode;
  public readonly defaultSettings: InboxAISettings = {
    inbox_ai_mode: InboxAIAutoreplyMode.Send,
    inbox_ai_delay_minutes: 30,
    scheduled_autoreply_enabled: false,
    scheduled_autoreply_start_time: '21:00',
    scheduled_autoreply_end_time: '09:00',
    inbox_ai_attribution_enabled: true,
    inbox_ai_attribution_message: 'This message was written by my AI assistant.',
    inbox_ai_escalation_policy: 'Allow escalation only if the guest is locked out.',
    inbox_ai_escalation_message: 'Please call me on 1234567890',
    inbox_ai_thread_stages: ['prebooking', 'precheckin', 'staying'],
    scheduled_autoreply_days_of_week: this.allDays,
  };

  currentGroup: InboxAISettingsGroup;
  settingsGroups$: Observable<InboxAISettingsGroup[]>;
  possibleProperties: any[] = []; // For backward compatibility
  isLoading = true;
  saveStatus = 0; // 0: none, 1: saving, 2: saved, -1: error
  showEveryDay = true;
  selectedTabIndex = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private titleService: TitleService,
    private toast: ToastNotificationsService,
    private settingsService: SettingsService,
    private router: Router,
    private route: ActivatedRoute,
    private segmentIoService: SegmentIoService,
    private propertiesService: PropertiesService,
    private hostsService: HostsService,
    private platformsService: PlatformsService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.titleService.manuallySet('Inbox AI Settings');
    this.initializeSettingsAndGroup();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  patchSetting<K extends keyof InboxAISettings>(settingName: K, value: InboxAISettings[K], successMessage: string = '') {
    if (!this.currentGroup?.id) return;

    this.saveStatus = 1; // saving
    this.settingsService.patchInboxAISettings(this.currentGroup.id, {
      settings: { [settingName]: value } as Partial<InboxAISettings>
    }).subscribe({
      next: () => {
        this.saveStatus = 2; // saved
        if (successMessage) {
          this.toast.open(successMessage, 'Dismiss', NotificationType.Success);
        }
        setTimeout(() => this.saveStatus = 0, 3000);
      },
      error: (error) => {
        this.saveStatus = -1; // error
        // Only show generic error if there's no validation error message
        if (!error?.error?.message) {
          this.toast.open('Failed to save settings', 'Dismiss', NotificationType.Error);
        }
      },
    });
  }

  initializeSettingsAndGroup() {
    this.isLoading = true;
    this.settingsGroups$ = this.settingsService.getInboxAISettingsGroups();

    // Combine the groups observable with the route params
    combineLatest([
      this.settingsGroups$,
      this.route.params
    ]).pipe(
      takeUntil(this.destroy$),
      switchMap(([groups, params]) => {
        const groupId = params['groupId'];
        if (!groupId) {
          this.isLoading = false;
          return [];
        }

        // Always load the specific group's settings, even if it's not in the groups list yet
        return this.settingsService.getInboxAISettings(groupId).pipe(
          map(group => {
            // Initialize the scoping fields if they don't exist, and ensure platforms is an array of strings
            this.currentGroup = {
              ...group,
              settings: { ...this.defaultSettings, ...group.settings },
              platforms: this.normalizePlatforms(group.platforms || []),
              all_platforms: group.all_platforms || false,
              hosts: group.hosts || [],
              all_hosts: group.all_hosts || false,
              properties: group.properties || [],
              all_properties: group.all_properties || false
            };
            this.updateEveryDayState();
            this.isLoading = false;
            return [this.currentGroup];
          })
        );
      })
    ).subscribe({
      error: (error) => {
        this.isLoading = false;
        this.toast.open('Failed to load settings', 'Dismiss', NotificationType.Error);
      }
    });
  }

  // Helper method to normalize platforms to string array
  private normalizePlatforms(platforms: any[]): string[] {
    // Remove duplicates while standardizing to strings
    const platformKeys = new Set<string>();

    platforms.forEach(platform => {
      if (typeof platform === 'string') {
        platformKeys.add(platform);
      } else if (platform && typeof platform === 'object') {
        if (platform.id) platformKeys.add(platform.id);
        else if (platform.key) platformKeys.add(platform.key);
      }
    });

    return Array.from(platformKeys);
  }

  toggleAutoReply(event: MatSlideToggleChange) {
    if (!this.currentGroup?.settings) return;

    this.currentGroup.settings.scheduled_autoreply_enabled = event.checked;
    this.patchSetting('scheduled_autoreply_enabled', event.checked);
  }

  updateGroupName() {
    if (!this.currentGroup) return;

    this.saveStatus = 1; // saving
    this.settingsService.patchInboxAISettings(this.currentGroup.id, {
      name: this.currentGroup.name
    }).subscribe({
      next: () => {
        this.saveStatus = 2; // saved
        this.toast.open('Group name updated', 'Dismiss', NotificationType.Success);
        setTimeout(() => this.saveStatus = 0, 3000);
      },
      error: (error) => {
        this.saveStatus = -1; // error
        if (!error?.error?.message) {
          this.toast.open('Failed to update group name', 'Dismiss', NotificationType.Error);
        }
      },
    });
  }

  onGroupChange(event: MatSelectChange) {
    if (event.value === 'new') {
      this.createNewGroup();
    } else {
      this.selectedTabIndex = 0; // Reset to the first tab
      this.router.navigate(['/gx/inbox-ai/settings', event.value]);
    }
  }

  createNewGroup() {
    this.settingsService.createSettingsGroup().subscribe({
      next: (group) => {
        this.router.navigate(['/gx/inbox-ai/settings', group.id]);
      },
      error: () => {
        this.toast.open('Failed to create new group', 'Dismiss', NotificationType.Error);
      }
    });
  }

  updateEveryDayState() {
    if (!this.currentGroup?.settings?.scheduled_autoreply_days_of_week) {
      this.currentGroup.settings.scheduled_autoreply_days_of_week = [...this.defaultSettings.scheduled_autoreply_days_of_week];
    }
    this.showEveryDay = this.currentGroup.settings.scheduled_autoreply_days_of_week.length === this.allDays.length;
  }

  toggleEveryDay(checked: boolean) {
    if (!this.currentGroup?.settings) return;

    if (!this.currentGroup.settings.scheduled_autoreply_days_of_week) {
      this.currentGroup.settings.scheduled_autoreply_days_of_week = [...this.defaultSettings.scheduled_autoreply_days_of_week];
    }

    if (checked) {
      this.currentGroup.settings.scheduled_autoreply_days_of_week = [...this.allDays];
    } else {
      this.currentGroup.settings.scheduled_autoreply_days_of_week = [];
    }

    this.showEveryDay = checked;
    this.patchSetting('scheduled_autoreply_days_of_week', this.currentGroup.settings.scheduled_autoreply_days_of_week);
  }

  toggleDay(day: string, checked: boolean) {
    if (!this.currentGroup?.settings) return;

    if (!this.currentGroup.settings.scheduled_autoreply_days_of_week) {
      this.currentGroup.settings.scheduled_autoreply_days_of_week = [...this.defaultSettings.scheduled_autoreply_days_of_week];
    }

    if (checked) {
      if (!this.currentGroup.settings.scheduled_autoreply_days_of_week.includes(day)) {
        this.currentGroup.settings.scheduled_autoreply_days_of_week.push(day);
      }
    } else {
      this.currentGroup.settings.scheduled_autoreply_days_of_week = this.currentGroup.settings.scheduled_autoreply_days_of_week.filter(d => d !== day);
    }

    this.updateEveryDayState();
    this.patchSetting('scheduled_autoreply_days_of_week', this.currentGroup.settings.scheduled_autoreply_days_of_week);
  }

  toggleThreadStage(stage: InboxAIThreadStage, checked: boolean) {
    if (!this.currentGroup?.settings) return;

    if (!this.currentGroup.settings.inbox_ai_thread_stages) {
      this.currentGroup.settings.inbox_ai_thread_stages = [...this.defaultSettings.inbox_ai_thread_stages];
    }

    if (checked) {
      if (!this.currentGroup.settings.inbox_ai_thread_stages.includes(stage)) {
        this.currentGroup.settings.inbox_ai_thread_stages.push(stage);
      }
    } else {
      this.currentGroup.settings.inbox_ai_thread_stages = this.currentGroup.settings.inbox_ai_thread_stages.filter(s => s !== stage);
    }

    this.patchSetting('inbox_ai_thread_stages', this.currentGroup.settings.inbox_ai_thread_stages);
  }

  getHours(timeString: string): number {
    if (!timeString) return 0;
    return parseInt(timeString.split(':')[0], 10);
  }

  getMinutes(timeString: string): number {
    if (!timeString) return 0;
    return parseInt(timeString.split(':')[1], 10);
  }

  updateStartTime(event: { hours: number; minutes: number }) {
    if (!this.currentGroup?.settings) return;

    this.currentGroup.settings.scheduled_autoreply_start_time = `${event.hours.toString().padStart(2, '0')}:${event.minutes.toString().padStart(2, '0')}`;
    this.patchSetting('scheduled_autoreply_start_time', this.currentGroup.settings.scheduled_autoreply_start_time);
  }

  updateEndTime(event: { hours: number; minutes: number }) {
    if (!this.currentGroup?.settings) return;

    this.currentGroup.settings.scheduled_autoreply_end_time = `${event.hours.toString().padStart(2, '0')}:${event.minutes.toString().padStart(2, '0')}`;
    this.patchSetting('scheduled_autoreply_end_time', this.currentGroup.settings.scheduled_autoreply_end_time);
  }

  saveSettings(successMessage: string = '') {
    if (!this.currentGroup) {
      this.toast.open('Unable to save settings: group not loaded', 'Dismiss', NotificationType.Error);
      return;
    }

    this.saveStatus = 1; // saving
    this.settingsService.saveInboxAISettings(this.currentGroup).subscribe({
      next: () => {
        this.saveStatus = 2; // saved
        if (successMessage) {
          this.toast.open(successMessage, 'Dismiss', NotificationType.Success);
        }
        setTimeout(() => this.saveStatus = 0, 3000);
      },
      error: (error) => {
        this.saveStatus = -1; // error
        // Only show generic error if there's no validation error message
        if (!error?.error?.message) {
          this.toast.open('Failed to save settings', 'Dismiss', NotificationType.Error);
        }
      },
    });
  }

  handleScopeChange(event: RuleScope) {
    if (!this.currentGroup) return;

    const scopeUpdate: Partial<InboxAISettingsGroup> = {
      all_platforms: event.all_platforms,
      all_hosts: event.all_hosts,
      all_properties: event.all_properties,
      platforms: this.normalizePlatforms(event.platforms),
      hosts: event.hosts,
      properties: event.properties
    };

    this.currentGroup.all_platforms = event.all_platforms;
    this.currentGroup.all_hosts = event.all_hosts;
    this.currentGroup.all_properties = event.all_properties;
    this.currentGroup.platforms = this.normalizePlatforms(event.platforms);
    this.currentGroup.hosts = event.hosts;
    this.currentGroup.properties = event.properties;

    // Auto-save when scope changes
    this.saveStatus = 1; // saving
    this.settingsService.patchInboxAISettings(this.currentGroup.id, scopeUpdate).subscribe({
      next: () => {
        this.saveStatus = 2; // saved
        setTimeout(() => this.saveStatus = 0, 3000);
      },
      error: (error) => {
        this.saveStatus = -1; // error
        if (!error?.error?.message) {
          this.toast.open('Failed to save scope settings', 'Dismiss', NotificationType.Error);
        }
      },
    });
  }

  loadProperties() {
    this.propertiesService.getScopingProperties([], null, null, 5000, false).subscribe((res) => {
      this.possibleProperties = res.data;
    });
  }

  deleteSettingsGroupPrompt() {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        item: 'Settings group',
        itemName: this.currentGroup?.name || 'this settings group'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteSettingsGroup();
      }
    });
  }

  deleteSettingsGroup() {
    if (!this.currentGroup?.id) return;

    this.settingsService.deleteSettingsGroup(this.currentGroup.id).subscribe({
      next: () => {
        this.toast.open('Settings group deleted', 'Dismiss', NotificationType.Success);
        this.router.navigate(['/gx/inbox-ai']);
      },
      error: () => {
        this.toast.open('Failed to delete settings group', 'Dismiss', NotificationType.Error);
      }
    });
  }

  copySettingsGroup() {
    if (!this.currentGroup?.id) return;

    this.settingsService.copySettingsGroup(this.currentGroup.id).subscribe({
      next: (group) => {
        if (group.id) {
          this.selectedTabIndex = 0; // Reset to the first tab
          this.router.navigate(['/gx/inbox-ai/settings', group.id]);
          this.toast.open('Settings group copied', 'Dismiss', NotificationType.Success);
        }
      },
      error: () => {
        this.toast.open('Failed to copy settings group', 'Dismiss', NotificationType.Error);
      }
    });
  }
}
